import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import NoAccounts from '@mui/icons-material/NoAccounts';
import AccountCircle from '@mui/icons-material/AccountCircle';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  Button,
  TableContainer,
} from '@mui/material';
import { getHorseAccess } from '../../../../../redux/horseAccess/actions';
import { updateBarnHorse } from '../../../../../redux/barn/actions';

import { primary } from '../../../../../utils/brandColors';

import AddEditModal from './components/AddEditModal';
import { formatDateString } from '../../../../../utils/dateHelper';
import useDebounce from '../../../../../utils/useDebounce';

const HorseAccessComponent = ({
  id,
  isAdmin,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    horseAccess,
    isFetchingHorseAccess,
  } = useSelector((state) => state.horseAccess);
  const {
    currentHorseLodging,
    isFetchingBarnHorse,
  } = useSelector((state) => state.horse);
  const {
    selectedBarn,
    questriSubscription,
  } = useSelector((state) => state.barn);

  // State
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'endDate',
      sort: 'desc',
    },
  ]);
  const [snackbar, setSnackbar] = React.useState(null);
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseAccess, setSelectedHorseAccess] = React.useState(null);
  const [showInfoModal, setShowInfoModal] = React.useState(false);
  const [price, setPrice] = React.useState('35');

  const debouncedPrice = useDebounce(price, 600);

  React.useEffect(() => {
    dispatch(getHorseAccess(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  React.useEffect(() => {
    if (currentHorseLodging.createBillableForPaidClientAccessSeat) {
      dispatch(updateBarnHorse(selectedBarn.id, id, { paidClientAccessSeatBillingAmount: parseFloat(debouncedPrice).toFixed(2) }))
        .then(() => {
          setSnackbar({
            children: 'Success! Client Experience billable amount updated.',
            severity: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setSnackbar({
            children: error.message || 'There was an error updating this Client Experience billable amount.',
            severity: 'error',
          });
        });
    }
  }, [debouncedPrice])

  React.useEffect(() => {
    if (currentHorseLodging) {
      const isMonthly = questriSubscription
        && ((questriSubscription.currentPeriodEnd.getTime()) - (questriSubscription.currentPeriodStart.getTime()) < 3456000000)
      setPrice((currentHorseLodging.paidClientAccessSeatBillingAmount
        && currentHorseLodging.paidClientAccessSeatBillingAmount.toString())
        || (isMonthly
          ? '35'
          : '420'))
    }
  }, [currentHorseLodging, questriSubscription])

  const handleAddClick = () => {
    setSelectedHorseAccess(null);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    // reload
    dispatch(getHorseAccess(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  };

  return (
    <Card>
      <CardHeader
        sx={{
          backgroundColor: currentHorseLodging.paidClientAccessSeat
            ? primary[500]
            : 'inherit',
          color: currentHorseLodging.paidClientAccessSeat
            ? 'white'
            : 'inherit',
          alignItems: 'flex-start',
        }}
        avatar={<Avatar><GroupIcon /></Avatar>}
        action={(isAdmin && (
          <div>
            <Tooltip title="About Client Experience">
              <IconButton
                sx={{
                  color: currentHorseLodging.paidClientAccessSeat
                    ? 'white'
                    : 'primary',
                }}
                onClick={() => setShowInfoModal(true)}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              aria-label="add user access"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                color: currentHorseLodging.paidClientAccessSeat
                  ? 'black'
                  : 'white',
                backgroundColor: currentHorseLodging.paidClientAccessSeat
                  ? 'white'
                  : primary[500],
              }}
            >
              Add User
            </Button>
          </div>
        ))}
        title="Manage Client Experience"
        subheaderTypographyProps={{
          sx: {
            textWrap: 'auto',
            color: currentHorseLodging.paidClientAccessSeat
              ? 'white'
              : 'inherit',
          },
        }}
        subheader={(
          <>
            <Typography variant="caption" component="div" sx={{ textWrap: 'auto' }}>
              Enabling Questri client experience (paid seat) will provide <b>clients</b> with full access to
              Questri Mobile and its features.
            </Typography>
            <Typography variant="caption" component="div" sx={{ textWrap: 'auto' }}>
              Questri mobile make it easier for you to communicate with your clients and schedule lessons,
              rides, and shows.
            </Typography>
            <FormControlLabel
              sx={{
                width: '100%',
                mb: 0,
              }}
              control={(
                <Switch
                  color="secondary"
                  checked={!!currentHorseLodging.paidClientAccessSeat}
                  disabled={isFetchingBarnHorse || isFetchingHorseAccess}
                  onChange={(e) => {
                    dispatch(updateBarnHorse(selectedBarn.id, id, { paidClientAccessSeat: e.target.checked }))
                      .then(() => {
                        setSnackbar({
                          children: e.target.checked
                            ? 'Success! Clients with access to this horse now have full access to Questri mobile.'
                            : 'Success! Paid client experience has been removed. Clients will no longer have access to Questri mobile.',
                          severity: 'success',
                        });
                      })
                      .catch((error) => {
                        console.error(error);
                        setSnackbar({
                          children: error.message || 'There was an error switching this horse to a paid client experience seat.',
                          severity: 'error',
                        });
                      });
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              )}
              label={`Questri Client Experience Seat  (${currentHorseLodging.paidClientAccessSeat
                ? 'ENABLED'
                : 'DISABLED'})`}
            />
            {/* {
              !!currentHorseLodging.paidClientAccessSeat && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar
                    size="small"
                    sx={{
                      mr: 2,
                      backgroundColor: '#ffffff',
                      height: 28,
                      width: 28,
                    }}
                  ><AttachMoney color="success" />
                  </Avatar>
                  <Box>
                    <FormControlLabel
                      sx={{
                        width: '100%',
                        mb: 0,
                      }}
                      control={(
                        <Switch
                          color="secondary"
                          size="small"
                          checked={!!currentHorseLodging.createBillableForPaidClientAccessSeat}
                          disabled={isFetchingBarnHorse || isFetchingHorseAccess}
                          onChange={(e) => {
                            dispatch(updateBarnHorse(selectedBarn.id, id, { createBillableForPaidClientAccessSeat: e.target.checked }))
                              .then(() => {
                                setSnackbar({
                                  children: e.target.checked
                                    ? 'Success! Questri will automatically create a billable for
                                    this horse cooresponding with the billing period of your Questri subscription.'
                                    : 'Success! Questri will no longer create billables on this horse for the client experience seat.',
                                  severity: 'success',
                                });
                              })
                              .catch((error) => {
                                console.error(error);
                                setSnackbar({
                                  children: error.message || 'There was an error switching
                                  this horse automatically create client experience seat billable.',
                                  severity: 'error',
                                });
                              });
                          }}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      )}
                      label={`Create Horse Billable (${currentHorseLodging.createBillableForPaidClientAccessSeat
                        ? 'ENABLED'
                        : 'DISABLED'})`}
                    />
                    <FormHelperText sx={{ color: '#ffffff' }}>
                      Helpful when billing clients for their access to Questri. Billed according to your Questri subscription
                      billing period (One billable per year for yearly payments, one billable per month for monthly payments)
                    </FormHelperText>
                    {
                      !!currentHorseLodging.createBillableForPaidClientAccessSeat && (
                        <TextField
                          sx={{
                            mt: 2,
                            color: '#ffffff',
                            fieldset: { borderColor: '#fff' },
                          }}
                          size="small"
                          id="billable-price"
                          label="Billable Amount"
                          value={price}
                          onChange={(e) => {
                            if (/^\d+(\.\d{0,2})?$/.test(e.target.value) || e.target.value === '') {
                              setPrice(e.target.value);
                            }
                          }}
                          InputLabelProps={{ sx: { color: '#fff' } }}
                          FormHelperTextProps={{ sx: { color: '#fff' } }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ color: '#fff !important' }}>$</InputAdornment>,
                            sx: {
                              borderColor: '#fff',
                              color: '#fff',
                            },
                          }}
                          helperText="The amount to bill client for access to this horse and Questri mobile"
                        />
                      )
                    }
                  </Box>
                </Box>
              )
            } */}
          </>
        )}
      />
      {
        !isAdmin
          ? <CardContent>You should not be here... Insufficient permisions</CardContent>
          : (
            <CardContent>
              <Pagination count={horseAccess.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingBarnHorse || isFetchingHorseAccess} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      >Avatar
                      </TableCell>
                      <TableCell align="left">Name</TableCell>
                      <TableCell align="left">Account</TableCell>
                      <TableCell align="left">Access Type</TableCell>
                      <TableCell align="left">Note</TableCell>
                      <TableCell align="left">Ownership</TableCell>
                      <TableCell align="left">Access Start</TableCell>
                      <TableCell align="left">Access End</TableCell>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      >Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    style={{
                      height: 100,
                      overflowY: 'auto',
                      overflowX: 'auto',
                    }}
                  >
                    {horseAccess.content.map((ha) => (
                      <TableRow key={ha.id}>
                        <TableCell
                          align="center"
                          style={{
                            maxWidth: 90,
                            minWidth: 90,
                          }}
                        ><IconButton aria-haspopup="false"> <Avatar src={ha.user.avatar} /> </IconButton>
                        </TableCell>
                        <TableCell align="left">{ha.user.userFullName}</TableCell>
                        <TableCell align="left">
                          {
                            ha.user.auth0UserId
                              ? (<AccountCircle color="primary" />)
                              : (<Tooltip title="No account for this user. Make sure they have an email and have logged into Questri"><NoAccounts color="secondary" /></Tooltip>)
                          }
                        </TableCell>
                        <TableCell align="left">{ha.type}</TableCell>
                        <TableCell align="left">
                          {ha.accessNote && <Typography variant="caption" component="div">{`"${ha.accessNote}"`}</Typography>}
                        </TableCell>
                        <TableCell align="left">
                          {ha.owner && `${ha.ownershipPercentage}%`}
                          {ha.owner && ha.ownershipNotes && <Typography variant="caption" component="div">{`"${ha.ownershipNotes}"`}</Typography>}
                        </TableCell>
                        <TableCell align="left">{formatDateString(ha.startDate)}</TableCell>
                        <TableCell align="left">{ha.endDate && formatDateString(ha.endDate)}</TableCell>
                        <TableCell
                          align="center"
                          style={{
                            maxWidth: 90,
                            minWidth: 90,
                          }}
                        >
                          <IconButton
                            style={{ padding: 4 }}
                            onClick={
                              () => { setSelectedHorseAccess(ha); setOpenAddEditModal(true); }
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          )
      }
      {!!snackbar && (
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open
          onClose={() => { setSnackbar(null); }}
          autoHideDuration={10000}
        >
          <Alert {...snackbar} onClose={() => { setSnackbar(null); }} />
        </Snackbar>
      )}
      <AddEditModal
        open={openAddEditModal}
        horseAccess={selectedHorseAccess}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
        aria-describedby="info-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 400,
            maxWidth: 800,
            bgcolor: 'background.paper',
            boxShadow: 24,
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          <Typography variant="h5" component="h2" id="info-modal-title" paddingBottom>
            About Questri Client Experience
          </Typography>
          <Typography id="info-modal-description" paddingBottom>
            When you give a user access to a horse, that user will be available for
            you to use as a rider, or other horse tasks related to the horse.
            This is usefull for organization on the barn board where
            rides, lessons, and other activities can be grouped by client.
          </Typography>
          <Typography paddingBottom>
            Giving a user horse access <b>DOES NOT</b> give that user privilages to
            view/interact with the horse in Questri (web or mobile). Advanced features
            for both the client and Questri Barn are only enabled when a paid client experience
            seat is active on the horse.
          </Typography>
          <Divider
            sx={{
              mb: 1,
              mt: 1,
            }}
          />
          <Typography variant="h5" component="h2" paddingBottom>
            Questri Client Experience Seat <small>(paid)</small>
          </Typography>
          <Typography paddingBottom>
            When you enable a paid client experience seat for a horse, you unlock
            many feature for both you and your client to make their experience with
            questri and your Questri Barn much more productive and enjoyable.
          </Typography>
          <Typography paddingBottom>
            Note that only users with the <b>Client</b> type gain access to
            these features, all others access types get limited access to the horse depending on their role.
          </Typography>
          <Typography variant="h5" component="h2" paddingBottom>
            Admin Features:
          </Typography>
          <ul>
            <li>Share riding and lesson schedules with clients, take schedule change requests</li>
            <li>Develop and share lesson plans, skills, rankings, etc.</li>
            <li>Share horse notes with clients (photos, videos, show results, etc.)</li>
            <li>
              <b>Questri Announcements & Group Chats</b>, a mechanism for you to
              share announcements with all your clients (upcoming shows, administrative changes, etc.)
              and comminicate in chat boards
            </li>
          </ul>
          <Typography variant="h5" component="h2" paddingBottom>
            Client Features:
          </Typography>
          <ul>
            <li>Access to Questri Mobile</li>
            <li>View horse info and select horse notes (lessons, pictures, videos)</li>
            <li>View schedule, request schedule changes</li>
            <li>View shows, results, rankings/skills/plans</li>
            <li>
              Access to <b>Questri Announcements & Group Chats</b>, a mechanism for clients to view
              announcements and posts from the organization, as well as communicate
              on chat boards with others.
            </li>
            <li>View and manage assigned tasks (Show sign up, billing, etc.)</li>
          </ul>
        </Box>
      </Modal>
    </Card>
  );
};

HorseAccessComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

HorseAccessComponent.defaultProps = {};

export default HorseAccessComponent;
